import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import TwoFactorModal from "./TwoFactorModal";
import { startFlow, handleCodeSubmit, resendCode } from "./twoFactorRunner";

export const TwoFactorContext = createContext(null);

/**
 * Provider exposing runWith2FA(config) and rendering the single global OTP
 * modal. Wrap the application with it so any component can trigger the flow.
 * @param {Object} props
 * @param {React.ReactNode} props.children - The application tree
 * @returns {React.ReactElement}
 */
export function TwoFactorProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const flowRef = useRef(null);

    const cancelFlow = useCallback(() => {
        setOpen(false);
        setError(null);
        if (flowRef.current) flowRef.current.reject(new Error("2fa_cancelled"));
        flowRef.current = null;
    }, []);

    const runWith2FA = useCallback(
        (config) => startFlow(config, { setOpen, setError, flowRef }),
        []
    );
    const submitCode = useCallback(
        (code) => handleCodeSubmit(code, { flowRef, setSubmitting, setError, setOpen }),
        []
    );

    const value = useMemo(() => ({ runWith2FA }), [runWith2FA]);

    return (
        <TwoFactorContext.Provider value={value}>
            {children}
            <TwoFactorModal
                open={open}
                submitting={submitting}
                error={error}
                onSubmit={submitCode}
                onResend={() => resendCode(flowRef, setError)}
                onCancel={cancelFlow}
            />
        </TwoFactorContext.Provider>
    );
}
